import { FormattedMessage } from 'react-intl';

import type { BotCommandSuggestion } from 'mastodon/utils/bot_commands';
import { numberRange, partName } from 'mastodon/utils/bot_commands';

import classes from './autosuggest_bot_command.module.scss';

const HANGUL_START = 0xac00;
const HANGUL_END = 0xd7a3;

/**
 * 을 after a final consonant, 를 after a vowel; 을(를) when the word does not
 * end in Hangul (a number, a Latin letter)
 */
const objectParticle = (word: string) => {
  const code = word.charCodeAt(word.length - 1);

  if (code < HANGUL_START || code > HANGUL_END) return '을(를)';

  return (code - HANGUL_START) % 28 === 0 ? '를' : '을';
};

/**
 * One suggestion for a bot command written in square brackets. Fixed words
 * show with their description; parts written freely (text, number) as what
 * to write, e.g. "아이템명을 입력해 주세요.", "개수(1~999)를 입력해 주세요."
 */
export const AutosuggestBotCommand: React.FC<{
  suggestion: BotCommandSuggestion;
}> = ({ suggestion }) => {
  const { node } = suggestion;
  const asks = node.type === 'text' || node.type === 'number';

  let content;

  if (suggestion.kind === 'keyword') {
    content = (
      <>
        <strong>{suggestion.label}</strong>
        {suggestion.description && (
          <span className={classes.description}>{suggestion.description}</span>
        )}
      </>
    );
  } else if (asks) {
    const name = partName(node);
    const range = numberRange(node);

    content = (
      <span className={classes.slot}>
        <FormattedMessage
          id='bot_command_suggestion.enter'
          defaultMessage='Enter {name}'
          values={{
            name: range ? `${name}(${range})` : name,
            particle: objectParticle(name),
          }}
        />
      </span>
    );
  } else {
    content = (
      <>
        <span className={classes.slot}>{suggestion.label}</span>
        {suggestion.description && (
          <span className={classes.description}>{suggestion.description}</span>
        )}
      </>
    );
  }

  return (
    <div className={`autosuggest-hashtag ${classes.row}`}>
      <div className='autosuggest-hashtag__name'>{content}</div>

      {suggestion.acct && (
        <div className={classes.acct}>@{suggestion.acct}</div>
      )}
    </div>
  );
};
